import React, { useEffect, useRef, useState } from "react";
import { AspectRatio, Box, TabList, TabPanel, TabPanels, Tabs, type SpaceProps } from "@chakra-ui/react";
import { AnimatePresence, useInView } from "framer-motion";
import { type Theme } from "contexts/color-theme";
import { ChakraBox } from "ui/chakra-framer";
import { TextColumn } from "ui/text";
import { ProgressTab, type ProgressTabProps } from "./progress-tab";

const fadeIn = {
  initial: { opacity: 0.5 },
  animate: { opacity: 1, transition: { duration: 0.4 } },
  exit: { opacity: 0.5, transition: { duration: 0.4 } },
};

export const progressTabMediaRatio = { base: 1 / 1, sm: 1318 / 700, md: 1318 / 587 };

export type ProgressTabsProps = {
  /**
   * The color theme of the tab content.
   *
   * @default "light"
   */
  theme?: Theme;

  /**
   * The tabs to display. Each has a progress bar when active.
   */
  tabs: ProgressTabProps[];

  /**
   * The duration of the progress bar animation in seconds.
   *
   * @default 6
   */
  progressDuration?: number;

  /**
   * Callback when the tab changes.
   */
  onChangeTab?: (tabIndex: number) => void;
} & SpaceProps;

export function ProgressTabs({
  tabs,
  theme = "light",
  progressDuration = 6,
  onChangeTab,
  ...spaceProps
}: ProgressTabsProps) {
  const [activeTab, setActiveTab] = useState(0);
  const scope = useRef<HTMLDivElement>(null);
  const isInView = useInView(scope, { once: true });
  const activeTabContent = tabs[activeTab];

  useEffect(() => {
    onChangeTab?.(activeTab);
  }, [activeTab, onChangeTab]);

  return (
    <Box ref={scope} w="100%" {...spaceProps}>
      <Tabs
        index={activeTab}
        variant="unstyled"
        display="flex"
        flexDir="column"
        gap={{ base: "smaller", md: "big", xl: "bigger" }}
        onChange={(tabIndex) => {
          setActiveTab(tabIndex);
        }}
      >
        <TabPanels>
          {tabs.map((tab, index) => (
            <TabPanel key={tab.title} pos="relative" boxSize="100%" p={0}>
              <AnimatePresence initial={false}>
                <ChakraBox
                  key={tab.description}
                  variants={fadeIn}
                  initial="initial"
                  animate={activeTab === index ? "animate" : "exit"}
                  exit="exit"
                >
                  <AspectRatio
                    borderRadius="small"
                    ratio={progressTabMediaRatio}
                    w="100%"
                    overflow="hidden"
                    pos="relative"
                  >
                    {tab.media}
                  </AspectRatio>
                </ChakraBox>
              </AnimatePresence>
            </TabPanel>
          ))}
        </TabPanels>

        <TabList alignItems="start" gap="small">
          {tabs.map(({ onProgressEnd, ...tab }, index) => (
            <ProgressTab
              key={tab.description}
              // Do not begin progress animation until the component comes into view for the first time
              isActive={isInView && index === activeTab}
              theme={theme}
              progressDuration={progressDuration}
              onProgressEnd={() => {
                setActiveTab(index === tabs.length - 1 ? 0 : activeTab + 1);
              }}
              {...tab}
            />
          ))}
        </TabList>
      </Tabs>

      <TextColumn
        title={activeTabContent.title}
        description={activeTabContent.description}
        theme={theme}
        display={{ base: "flex", md: "none" }}
        mt="tiny"
      />
    </Box>
  );
}
